/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { Box } from "@mui/material";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import Table from "components/Table";
import usePaginationHook from "hooks/usePagination";
import { useSnackbar } from "hooks/useSnackbar";
import { setNumberFormat, sortArray } from "utils/miscUtils";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { getAllUsers, unblockCredit } from "services/miscService";
import OperatorSingleSelect from "components/OperatorSingleSelect";
import { getAllUsersTableCols } from "utils/tableHelper/extraTableHelper";
import InfoIcon from "@mui/icons-material/Info";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import { SimpleSelectInput } from "components/FormSelectInput";
import { useNavigate } from "react-router-dom";
import configData from "config/config.json";
import SearchIcon from "@mui/icons-material/Search";
import { SimpleTextInput } from "components/FormTextInput";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";

const SearchByOptions = [
  {
    id: "AUTO_TEAM_NAME",
    label: "AUTO TEAM NAME",
  },
  {
    id: "RGS_PLAYER_ID",
    label: "RGS PLAYER ID",
  },
  {
    id: "USER_ID",
    label: "USER ID",
  },
  {
    id: "COUNTRY_CODE",
    label: "COUNTRY CODE",
  },
];

const CreditPendingOptions = [
  { id: "true", label: "Credit Pending Only" },
];

const AllUsers = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [listData, setListData] = React.useState([]);
  const [columns, setColumns] = React.useState([]);
  const [orderDirection, setOrderDirection] = React.useState("asc");
  const [searchBy, setSearchBy] = React.useState(null);
  const [creditPending, setCreditPending] = React.useState(null);
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async ({
    search = null,
    searchBy = null,
    creditPending: creditPendingParam = null,
    ...rest
  }: {
    search?: any;
    searchBy?: any;
    creditPending?: any;
    pageNo?: number;
    pageSize?: number;
  } = {}) => {
    try {
      setLoading(true);
      const payload = {
        pageNo: rest.pageNo ?? pageNo,
        pageSize: rest.pageSize ?? pageSize,
      };

      if (search !== null || search !== "") payload["search"] = search;
      if (searchBy !== null || searchBy !== "") payload["searchBy"] = searchBy;
      if (creditPendingParam) payload["creditPending"] = true;
      const response = await getAllUsers(payload);
      setListData(
        (response.data?.items || []).map((item) => {
          const notValidInputs = [null, undefined];
          item["balance"] = setNumberFormat(item?.balance || 0, 4);
          item["isFtuText"] = !notValidInputs.includes(item.isFTU)
            ? item.isFTU
              ? "Yes"
              : "No"
            : "N/A";
          item["showEosText"] = !notValidInputs.includes(item.showEos)
            ? item.showEos
              ? "Yes"
              : "No"
            : "N/A";
          item["creditPendingText"] = item.creditPendingSince ? "Pending" : "";
          return item;
        }),
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error.message || "Failed fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  const onSearchClick = async () => {
    if (searchValue.length > 0 && searchBy) {
      onChangePage(defaultPageNo);
      await fetchData({
        pageNo: defaultPageNo,
        search: searchValue,
        searchBy,
        creditPending,
      });
    } else
      showSnackbar(
        "Please select search by then enter a search value",
        "error",
      );
  };

  // Drives the same searchBy/search state as the free-text search, via a friendlier dropdown.
  const onOperatorFilterChange = async (value: string | null) => {
    const nextSearchBy = value ? "OPERATOR_ID" : null;
    setSearchBy(nextSearchBy);
    _onSearch(value || "");
    onChangePage(defaultPageNo);
    await fetchData({
      pageNo: defaultPageNo,
      search: value || "",
      searchBy: nextSearchBy,
      creditPending,
    });
  };

  const onCreditPendingFilterChange = async (value: any) => {
    setCreditPending(value || null);
    onChangePage(defaultPageNo);
    await fetchData({
      pageNo: defaultPageNo,
      search: searchValue,
      searchBy,
      creditPending: value || null,
    });
  };

  const rowHref = (data: any) =>
    configData.routes.allUsers.userInfo + `?id=${data?._id}`;

  const handleInfoClick = (data: any) => {
    navigate(rowHref(data));
  };

  const handleUnblockClick = async (data: any) => {
    try {
      await unblockCredit(data._id);
      showSnackbar("Credit block cleared", "success");
      await fetchData({
        pageNo,
        search: searchValue,
        searchBy,
        creditPending,
      });
    } catch (error) {
      showSnackbar(error.message || "Failed to clear credit block", "error");
    }
  };

  const reset = () => {
    setSearchBy(null);
    setCreditPending(null);
    _onSearch("");

    if (pageNo !== defaultPageNo || pageSize !== defaultPageSize) {
      onChangePage(defaultPageNo);
      onPageSizeChange(defaultPageSize);
    } else {
      fetchData({
        pageNo: defaultPageNo,
        pageSize: defaultPageSize,
        search: "",
        searchBy: null,
        creditPending: null,
      });
    }
  };

  React.useEffect(() => {
    setColumns(getAllUsersTableCols());
    fetchData({ search: searchValue, searchBy, creditPending });
  }, [pageNo, pageSize]);

  return (
    <ContentShell title="All Users" loading={loading}>
      <Box>
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <Box style={{ width: 220 }}>
            <OperatorSingleSelect
              label="Operator"
              value={searchBy === "OPERATOR_ID" ? searchValue : null}
              onChange={onOperatorFilterChange}
            />
          </Box>
          <Box style={{ width: 180 }}>
            <SimpleSelectInput
              name="creditPending"
              placeholdertext="Credit Status"
              options={CreditPendingOptions}
              value={creditPending || ""}
              onChange={(e) => onCreditPendingFilterChange(e?.target.value)}
            />
          </Box>
          <Box style={{ width: 180 }}>
            <SimpleSelectInput
              name="searchBy"
              placeholdertext="Search By"
              options={SearchByOptions}
              value={searchBy || ""}
              onChange={(e) => setSearchBy(e?.target.value)}
            />
          </Box>
          <Box style={{ width: 180 }}>
            <SimpleTextInput
              value={searchValue}
              name="searchValue"
              placeholdertext="Search"
              onChange={(e) => _onSearch(e.target.value)}
            />
          </Box>
          <CustomButton startIcon={<SearchIcon />} onClick={onSearchClick}>
            Search
          </CustomButton>
          <CustomButton startIcon={<RestartAltIcon />} onClick={reset}>
            Reset
          </CustomButton>
        </Box>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          rowHref={rowHref}
          actions={({ data }) => (
            <Box style={{ display: "flex", flexDirection: "column", gap: 6, padding: "4px 0" }}>
              <CustomButton
                startIcon={<InfoIcon />}
                onClick={(e: any) => {
                  e.stopPropagation();
                  handleInfoClick(data);
                }}
              >
                Details
              </CustomButton>
              {data.creditPendingSince && sessionHelper.hasRole(PermittedRoles.ALL_USERS.UNBLOCK_CREDIT_ROLES) ? (
                <CustomButton
                  startIcon={<LockOpenIcon />}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    handleUnblockClick(data);
                  }}
                >
                  Unblock
                </CustomButton>
              ) : null}
            </Box>
          )}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          actionHandlers={{}}
        />
      </Box>
    </ContentShell>
  );
};

export default AllUsers;
